import React from 'react';
import { X, AlertTriangle, Trash2, Power } from 'lucide-react';

export type ConfirmVariant = 'danger' | 'primary' | 'warning';

interface ConfirmPopupProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  onConfirm: () => void;
}

const variantStyles: Record<ConfirmVariant, { icon: React.ReactNode; buttonClass: string }> = {
  danger: {
    icon: <Trash2 className="w-5 h-5 text-red-400" />,
    buttonClass: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500/50',
  },
  primary: {
    icon: <Power className="w-5 h-5 text-indigo-400" />,
    buttonClass: 'bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500/50',
  },
  warning: {
    icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
    buttonClass: 'bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500/50',
  },
};

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
  open,
  onClose,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
}) => {
  if (!open) return null;

  const style = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-sm rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <div className="flex items-start gap-4 p-6">
          <span className="flex-shrink-0 w-12 h-12 rounded-full bg-neutral-800 flex items-center justify-center">
            {style.icon}
          </span>
          <div className="flex-1 min-w-0">
            <h3 id="confirm-title" className="text-base font-semibold text-white">
              {title}
            </h3>
            <p className="mt-1.5 text-sm text-gray-400">{message}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 p-1 text-gray-500 hover:text-white rounded-md hover:bg-neutral-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 text-sm font-medium rounded-lg border border-neutral-600 text-gray-300 hover:bg-neutral-800 hover:border-neutral-500 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-900 ${style.buttonClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPopup;
